import {
	APP_PRODUCTION_URL,
	BASE_URL,
	BUSINESS,
	DEFAULT_BLOG_AUTHOR,
} from "./constants"
import { type ProductFaq } from "./product-faqs"

type BlogPostImage = {
	id: string
	altText: string | null
}

type BlogPostData = {
	title: string
	slug: string
	excerpt: string | null
	content: string
	publishedAt: string | Date | null
	updatedAt: string | Date
	author: { name: string | null } | null
	images: BlogPostImage[]
}

export function generateBlogPostingJsonLd(post: BlogPostData) {
	const siteUrl =
		process.env.NODE_ENV === "production" ? APP_PRODUCTION_URL : BASE_URL

	const jsonLd: Record<string, unknown> = {
		"@context": "https://schema.org",
		"@type": "BlogPosting",
		headline: post.title,
		url: `${siteUrl}/blog/${post.slug}`,
		datePublished: post.publishedAt ?? post.updatedAt,
		dateModified: post.updatedAt,
		publisher: {
			"@type": "Organization",
			name: BUSINESS.name,
			url: siteUrl,
		},
	}

	if (post.excerpt) {
		jsonLd.description = post.excerpt
	}

	jsonLd.author = {
		"@type": "Person",
		name: post.author?.name ?? DEFAULT_BLOG_AUTHOR,
	}

	if (post.images[0]) {
		jsonLd.image = `${siteUrl}/resources/blog-images/${post.images[0].id}`
	}

	return jsonLd
}

type ProductImage = {
	id: string
	altText: string | null
}

type ProductVariant = {
	id: string
	title: string
	price: number
	sku: string | null
	stock: number
}

type ProductData = {
	name: string
	description: string
	brand: string
	category: string
	price: number
	stock: number
	handle: string
	isUnique: boolean
	images: ProductImage[]
	variants: ProductVariant[]
}

export function generateProductJsonLd(product: ProductData) {
	const siteUrl =
		process.env.NODE_ENV === "production" ? APP_PRODUCTION_URL : BASE_URL

	const lowestPrice = product.variants.length
		? Math.min(...product.variants.map(v => v.price))
		: product.price
	const highestPrice = product.variants.length
		? Math.max(...product.variants.map(v => v.price))
		: product.price

	const totalStock = product.variants.length
		? product.variants.reduce((sum, v) => sum + v.stock, 0)
		: product.stock

	const availability =
		totalStock > 0
			? product.isUnique
				? "https://schema.org/LimitedAvailability"
				: "https://schema.org/InStock"
			: "https://schema.org/OutOfStock"

	const jsonLd: Record<string, unknown> = {
		"@context": "https://schema.org",
		"@type": "Product",
		name: product.name,
		description: product.description,
		brand: {
			"@type": "Brand",
			name: product.brand,
		},
		category: product.category,
		url: `${siteUrl}/product/${product.handle}`,
		image: product.images.map(
			img => `${siteUrl}/resources/product-images/${img.id}`,
		),
		offers: {
			"@type": "AggregateOffer",
			priceCurrency: "EUR",
			lowPrice: lowestPrice.toFixed(2),
			highPrice: highestPrice.toFixed(2),
			offerCount: product.variants.length || 1,
			availability,
			seller: {
				"@type": "Organization",
				name: BUSINESS.name,
			},
		},
	}

	if (product.isUnique) {
		jsonLd.additionalProperty = [
			{
				"@type": "PropertyValue",
				name: "Pieza única",
				value: "Hecha a mano, única en el mundo",
			},
		]
	}

	if (product.variants.length > 0) {
		jsonLd.offers = {
			...(jsonLd.offers as Record<string, unknown>),
			offers: product.variants.map(variant => ({
				"@type": "Offer",
				name: variant.title,
				price: variant.price.toFixed(2),
				priceCurrency: "EUR",
				sku: variant.sku,
				availability:
					variant.stock > 0
						? "https://schema.org/InStock"
						: "https://schema.org/OutOfStock",
				url: `${siteUrl}/product/${product.handle}`,
			})),
		}
	}

	return jsonLd
}

type RelatedProductData = {
	name: string
	handle: string
	images: { id: string }[]
}

export function generateItemListJsonLd(products: RelatedProductData[]) {
	const siteUrl =
		process.env.NODE_ENV === "production" ? APP_PRODUCTION_URL : BASE_URL

	return {
		"@type": "ItemList",
		name: "Productos relacionados",
		numberOfItems: products.length,
		itemListElement: products.map((product, index) => ({
			"@type": "ListItem",
			position: index + 1,
			item: {
				"@type": "Product",
				name: product.name,
				url: `${siteUrl}/product/${product.handle}`,
				...(product.images[0]
					? {
							image: `${siteUrl}/resources/product-images/${product.images[0].id}`,
						}
					: {}),
			},
		})),
	}
}

type BreadcrumbItem = {
	name: string
	url: string
}

export function generateBreadcrumbListJsonLd(items: BreadcrumbItem[]) {
	return {
		"@type": "BreadcrumbList",
		itemListElement: items.map((item, index) => ({
			"@type": "ListItem",
			position: index + 1,
			name: item.name,
			item: item.url,
		})),
	}
}

export function generateFaqPageJsonLd(faqs: ProductFaq[]) {
	return {
		"@context": "https://schema.org",
		"@type": "FAQPage",
		mainEntity: faqs.map(faq => ({
			"@type": "Question",
			name: faq.question,
			acceptedAnswer: {
				"@type": "Answer",
				text: faq.answer,
			},
		})),
	}
}
